import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { CreditCard, Calendar, X, ShieldCheck, Loader2 } from "lucide-react";

/** Installment chooser modal — opens from SolarPackages.
 *  Lists allowed terms for the package and starts a Stripe subscription
 *  Checkout in card-only mode that auto-cancels after N months.
 */
export default function InstallmentChooser({ pkg, onClose }) {
  const [opts, setOpts] = useState(null);
  const [pickedMonths, setPickedMonths] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!pkg) return;
    api.get(`/installments/options/${pkg.package_id}`)
      .then((r) => {
        setOpts(r.data);
        // Default to the longest term — usually the biggest conversion lever.
        const terms = r.data.terms || [];
        if (terms.length) setPickedMonths(terms[terms.length - 1].months);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load plans"));
  }, [pkg]);

  if (!pkg) return null;

  const proceed = async () => {
    if (!pickedMonths) { toast.error("Pick a term"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/installments/checkout", {
        package_id: pkg.package_id,
        months: pickedMonths,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center px-4" role="dialog" aria-modal="true" aria-labelledby="inst-title" onClick={onClose} data-testid="installment-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A66] text-white p-6 relative">
          <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 text-[#C9A356] text-xs uppercase tracking-widest mb-1">
            <CreditCard className="w-3 h-3" /> Payment plan
          </div>
          <h2 id="inst-title" className="font-heading text-xl">{pkg.name}</h2>
          <p className="text-sm text-white/70 mt-1">${opts?.total_price?.toLocaleString() || pkg.price.toLocaleString()} financed over your chosen term</p>
        </div>
        <div className="p-6">
          {!opts ? (
            <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#C9A356] mx-auto" /></div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 mb-4">
                {opts.terms.map((t) => (
                  <button
                    key={t.months}
                    onClick={() => setPickedMonths(t.months)}
                    data-testid={`term-${t.months}`}
                    aria-pressed={pickedMonths === t.months}
                    className={`text-left p-3 rounded-lg border-2 transition ${
                      pickedMonths === t.months
                        ? "border-[#C9A356] bg-[#FFFBF0] dark:bg-amber-900/10"
                        : "border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356]"
                    }`}
                  >
                    <div className="flex items-center gap-1.5 text-xs text-[#475569]">
                      <Calendar className="w-3 h-3" /> {t.months} months
                    </div>
                    <div className="font-heading text-xl font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums">
                      ${t.monthly.toFixed(2)}<span className="text-xs font-normal text-[#475569]">/mo</span>
                    </div>
                  </button>
                ))}
              </div>

              <div className="space-y-2 mb-4 text-xs">
                <Row icon={CreditCard} label="Card on file (auto-pay required)" />
                <Row icon={ShieldCheck} label="Secured by Stripe · cancel late with no surprise fees" />
                <Row icon={Calendar} label={`Auto-stops after ${pickedMonths || "—"} payments`} />
              </div>

              <button onClick={proceed} disabled={!pickedMonths || submitting} className="btn-accent w-full" data-testid="installment-proceed">
                {submitting ? "Redirecting…" : `Continue to checkout`}
              </button>
              <p className="text-[10px] text-center text-[#94A3B8] mt-2">Credit cards only · No early-payoff penalty</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[#475569] dark:text-slate-400">
      <Icon className="w-3.5 h-3.5 text-[#C9A356] shrink-0" /> {label}
    </div>
  );
}
